import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

import { sanitizeDrgCodes } from "./InpatientRulesetForm";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const CheckboxLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

export interface IUnitCapFormData {
  maxUnitsPerCode: number;
  providerId: string | null;
  payerId: number | null;
  cptCodes: string[];
  groupName: string;
  contractLanguage: string;
  isDefault: boolean;
}

export const INITIAL_UNIT_CAP_FORM_DATA: IUnitCapFormData = {
  maxUnitsPerCode: 1,
  providerId: null,
  payerId: null,
  cptCodes: [],
  groupName: "",
  contractLanguage: "",
  isDefault: false,
};

interface IUnitCapFormProps {
  title: string;
  submitLabel: string;
  formData: IUnitCapFormData;
  onChange: (data: IUnitCapFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** CLE-2524: applicability (TYPICAL vs SCOPED) is immutable — edit mode locks the checkbox. */
  scopeLocked?: boolean;
}

export function UnitCapForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: IUnitCapFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  // Unlike charge-based / lesser-of, a unit-cap rule is inherently payer-specific
  // (a payer publishes the list), so a payer must be chosen for a scoped rule — no
  // "All payers" option.
  const payerOptions = useMemo(() => {
    return (payers?.data ?? []).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
  }, [payers]);

  const handleChange = <K extends keyof IUnitCapFormData>(
    field: K,
    value: IUnitCapFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    formData.cptCodes.length > 0 &&
    formData.maxUnitsPerCode >= 1 &&
    (formData.isDefault ||
      (formData.providerId !== null && formData.payerId !== null)) &&
    !isSubmitting;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Message
        severity="info"
        className="mb-4"
        content={
          <div>
            A <strong>Unit Cap</strong> rule reimburses each listed code at most{" "}
            <code>max units</code> times per service, regardless of how many
            units are billed (a payer&apos;s &quot;once per service&quot; list).
            Extra units are zeroed. This is different from a Package (which
            bundles <em>different</em> codes under a trigger) and a Case Rate
            (which keeps the single highest-priced code across a set).
          </div>
        }
      />

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="maxUnitsPerCode">Max units per code</label>
            <InputNumber
              inputId="maxUnitsPerCode"
              value={formData.maxUnitsPerCode}
              onValueChange={(e) =>
                handleChange("maxUnitsPerCode", e.value ?? 1)
              }
              min={1}
              max={99}
              showButtons
            />
            <FieldHelp>
              How many units of each listed code the payer reimburses per
              service. 1 = &quot;once per service&quot; (the common case).
            </FieldHelp>
          </Field>
        </FieldGrid>

        {/* CLE-2524: checked = TYPICAL (inert template), unchecked = SCOPED. */}
        <CheckboxLine>
          <Checkbox
            inputId="isDefault"
            checked={formData.isDefault}
            disabled={scopeLocked}
            onChange={(e) => handleChange("isDefault", !!e.checked)}
          />
          <label htmlFor="isDefault">Typical rule</label>
          <FieldHelp>
            Template only — no provider or payer. Has no effect until copied
            into a contract.
          </FieldHelp>
        </CheckboxLine>

        {!formData.isDefault && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerId}
                options={providerOptions}
                onChange={(e) => handleChange("providerId", e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
            </Field>
          </FieldGrid>
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="cptCodes">CPT / HCPCS Codes</label>
            <Chips
              inputId="cptCodes"
              value={formData.cptCodes}
              onChange={(e) =>
                handleChange("cptCodes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="99213, 90471, G0121, 0272-0372"
              separator=","
            />
            <FieldHelp>
              Enter individual codes (99213, G0121) or ranges (10004-10012).
              Each code is capped independently.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional label (e.g. 'Aetna once-per-service list')"
            />
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="contractLanguage">Contract Language</label>
            <InputTextarea
              id="contractLanguage"
              value={formData.contractLanguage}
              onChange={(e) => handleChange("contractLanguage", e.target.value)}
              rows={3}
              placeholder="Optional — the contract clause this rule models"
            />
          </Field>
        </FieldGrid>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
